import { z } from 'zod';
import { classifyTimingShape, HOURLY_PRESET_PERIODS } from './timingShape';

export { HOURLY_PRESET_PERIODS };

/** YYYY-MM-DD date string (e.g. "2026-04-15"). */
export const DateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .describe('YYYY-MM-DD date string (e.g. "2026-04-15").');

/**
 * FHIR `date` or `dateTime` as stored verbatim on a resource: a `YYYY-MM-DD` day or an ISO 8601
 * datetime carrying a UTC offset (`Z` or `±HH:mm`). Offset-less local datetimes are rejected because
 * the string is persisted as-is, so a local time has no fixed instant. A refine rather than
 * `z.iso.datetime()` so the JSON Schema the LLM sees stays a plain string — Zod would otherwise emit
 * a `format` and a ~300-char `pattern` that `sanitizeSchemaForGemini` does not strip.
 */
export const FhirDateOrDateTimeSchema = z
  .string()
  .refine(
    (value) => z.iso.date().safeParse(value).success || z.iso.datetime({ offset: true }).safeParse(value).success,
    {
      message: 'Must be a YYYY-MM-DD date or an ISO 8601 datetime with a UTC offset (e.g. 2026-09-02T20:48:33Z)',
    },
  )
  .describe('YYYY-MM-DD date or ISO 8601 datetime with a UTC offset (e.g. "2026-09-02T20:48:33Z").');

/**
 * IANA timezone identifier (e.g. "America/New_York", "UTC", "Europe/Paris").
 * Validated by attempting to construct an `Intl.DateTimeFormat` with it —
 * the runtime throws `RangeError` for unknown zones. Catches typos like
 * `"Bogota/America"` or `"blah"` before they reach `date-fns-tz`, where
 * an invalid zone silently degrades to UTC and produces wrong date math.
 */
export const IanaTimezoneSchema = z.string().refine(
  (tz) => {
    try {
      new Intl.DateTimeFormat(undefined, { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Must be a valid IANA timezone (e.g. "America/New_York")' },
);

// ============================================================================
// FHIR Period Schema (used by timing schemas)
// ============================================================================

// Accepts Date instances (tRPC + superjson decode dates back to real Date on the
// server) AND ISO datetime strings (LLM structured output). The `_zod.toJSONSchema`
// override is Zod 4's documented hook for non-default JSON Schema rendering — without
// it, `z.toJSONSchema` throws "Date cannot be represented in JSON Schema" and breaks
// any LLM workflow whose output schema reaches a Date field.
const dateOrIsoString = (): z.ZodCoercedDate => {
  const schema = z.coerce.date();
  schema._zod.toJSONSchema = (): { type: 'string'; format: 'date-time' } => ({
    type: 'string',
    format: 'date-time',
  });
  return schema;
};

export const FhirPeriodSchema = z.object({
  start: dateOrIsoString().optional().describe('YYYY-MM-DD or ISO 8601 datetime when the period starts.'),
  end: dateOrIsoString().optional().describe('YYYY-MM-DD or ISO 8601 datetime when the period ends.'),
});

// ============================================================================
// FHIR Timing Schemas (for recurring tasks)
// https://hl7.org/fhir/R4/datatypes.html#Timing
// ============================================================================

export const FhirTimingWhenSchema = z
  .enum(['MORN', 'MORN.early', 'MORN.late', 'AFT', 'AFT.early', 'AFT.late', 'EVE', 'EVE.early', 'EVE.late', 'NIGHT'])
  .describe('FHIR Timing.when code marking a time of day (morning, afternoon, evening, night).');

export type FhirTimingWhen = z.infer<typeof FhirTimingWhenSchema>;

export const FhirTimingDayOfWeekSchema = z
  .enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])
  .describe('FHIR Timing.dayOfWeek code (mon|tue|wed|thu|fri|sat|sun).');

export type FhirTimingDayOfWeek = z.infer<typeof FhirTimingDayOfWeekSchema>;

/**
 * Ceiling on `Timing.repeat.count` ("ends after N times"). Bounds the count so
 * the day-view engine's finite occurrence walk (`COUNT_WALK_MAX_DAYS` in
 * `@liza/services`, sized to cover this many occurrences in the sparsest
 * once-weekly case) always resolves the Nth occurrence — otherwise a large
 * count silently reads as unbounded while the sentence still promises an end.
 */
export const MAX_OCCURRENCE_COUNT = 999;

// Fields shared by the timing schemas (`FhirTimingRepeatSchema`, `CanonicalTimingRepeatSchema`).
const timingRepeatFields = {
  boundsPeriod: FhirPeriodSchema.optional().describe('Optional start/end window during which the schedule applies.'),
  count: z
    .number()
    .int()
    .positive()
    .max(MAX_OCCURRENCE_COUNT)
    .optional()
    .describe('Total number of occurrences after which the schedule ends (FHIR Timing.repeat.count).'),
  frequency: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('Number of occurrences per period (e.g. 2 = twice per period).'),
  period: z.number().positive().optional().describe('Length of the recurrence window (e.g. 1).'),
  dayOfWeek: z
    .array(FhirTimingDayOfWeekSchema)
    .optional()
    .describe('Restrict the schedule to specific weekdays (e.g. ["mon","wed","fri"]).'),
  timeOfDay: z
    .array(z.string().regex(/^\d{2}:\d{2}$/))
    .optional()
    .describe('Specific times of day in HH:MM (e.g. ["08:00","20:00"]).'),
  when: z.array(FhirTimingWhenSchema).optional().describe('Time-of-day codes (MORN, AFT, EVE, NIGHT, …).'),
} as const;

// The cadence trio defines how often a schedule recurs and must travel together. A sparse
// shape like a bare `{ dayOfWeek: ['mon','fri'] }` is ambiguous (which weeks? how often?) and
// serializes to an incomplete `scheduled-timing` extension. Every producer already emits the
// full trio — the UI schedule picker (`schedulePicker.ts`) always sets frequency/period/periodUnit
// — so requiring it here costs real callers nothing and rejects the sparse shapes an LLM tends to emit.
// `dayOfWeek`/`timeOfDay`/`when` remain optional modifiers layered on top of the cadence.
const requireCadenceTrio = (r: { frequency?: number; period?: number; periodUnit?: string }): boolean =>
  r.frequency !== undefined && r.period !== undefined && r.periodUnit !== undefined;
const cadenceTrioMessage = {
  // `frequency` is occurrences-per-period, NOT a day count — spell that out, because a model
  // encoding "every day" otherwise reaches for frequency:7 instead of the daily shape below.
  // dayOfWeek selects which days; frequency is how many times the event recurs per period.
  message:
    'Timing.repeat must specify frequency, period, and periodUnit together. `frequency` is the number of occurrences per period, NOT a count of days. A once-daily schedule is frequency:1, period:1, periodUnit:"d", dayOfWeek:["mon","tue","wed","thu","fri","sat","sun"] — do NOT set frequency to the number of days (frequency:7, period:1, periodUnit:"d" means 7 occurrences per day, not once a day). N-times-daily is frequency:N. dayOfWeek/timeOfDay/when are optional modifiers on top of the cadence.',
};

// An end that precedes the start is an empty, always-inactive window — the day view would
// render nothing and the sentence still reads "until <end>". Reject it at every write path.
// `boundsPeriod` fields are coerced to `Date` before this runs, so a plain `>` compares instants.
const boundsPeriodOrdered = (r: { boundsPeriod?: { start?: Date; end?: Date } }): boolean => {
  const { start, end } = r.boundsPeriod ?? {};
  return !start || !end || start.getTime() <= end.getTime();
};
const boundsPeriodOrderedMessage = {
  message: 'boundsPeriod.end must not be before boundsPeriod.start.',
  path: ['boundsPeriod', 'end'],
};

export const FhirTimingRepeatSchema = z
  .object({
    ...timingRepeatFields,
    periodUnit: z
      .enum(['h', 'd', 'wk', 'mo', 'a'])
      .optional()
      .describe('Unit of the period: h (hours), d (days), wk (weeks), mo (months), a (years).'),
  })
  .refine(requireCadenceTrio, cadenceTrioMessage)
  .refine(boundsPeriodOrdered, boundsPeriodOrderedMessage)
  .describe(
    'FHIR Timing.repeat. The cadence trio (frequency, period, periodUnit) is required and defines how often the event recurs (e.g. frequency:2, period:1, periodUnit:"d" = twice a day). dayOfWeek/timeOfDay/when are optional modifiers that further constrain the cadence.',
  );

/**
 * Write-path variant of `FhirTimingRepeatSchema` that additionally rejects any
 * shape `classifyTimingShape` calls `nonCanonical`. Use this on every server
 * task-write schema so a newly stored timing is always one of the canonical
 * shapes — the backstop that stops the non-canonical population (see the #6122
 * audit) from regrowing after the backfill.
 *
 * Note it shares `FhirTimingRepeat`'s inferred TYPE: a Zod `.refine` is a
 * runtime check, invisible to TypeScript, so there is deliberately no separate
 * "canonical type". Canonicality is enforced at the write boundary, not encoded
 * in the type — readers (`extensionToTimingRepeat` and the day-view engine)
 * must keep using the permissive `FhirTimingRepeat`, since stored and legacy
 * data is non-canonical until the backfill runs and the engine reads several
 * legacy shapes leniently by design. A type-level canonical union is #5178's
 * scope. This schema also does NOT narrow `periodUnit` the way
 * `LlmTimingRepeatSchema` does: `'d'` is the canonical weekday encoding.
 */
export const CanonicalTimingRepeatSchema = FhirTimingRepeatSchema.refine(
  (r) => classifyTimingShape(r) !== 'nonCanonical',
  {
    message:
      'Timing.repeat must be a canonical schedule shape: a weekday cadence (periodUnit:"d" with optional dayOfWeek and matching when/timeOfDay), a days-per-week quota ({frequency:N (2-7), period:1, periodUnit:"wk"}), an interval ({frequency:1, period:N, periodUnit:"d"|"wk"|"mo"|"a"}), or an hourly preset (periodUnit:"h", period 1/2/4/6/8/12). See docs/design-decisions/2026-07-01-canonical-care-plan-timing-shapes.md.',
  },
);

export type FhirTimingRepeat = z.infer<typeof FhirTimingRepeatSchema>;

export const FhirTimingSchema = z.object({
  repeat: FhirTimingRepeatSchema.optional(),
});

export type FhirTiming = z.infer<typeof FhirTimingSchema>;
